import * as logger from 'loglevel'
import { buildFormActionMessage } from '../../Utils/actionPayloadEnvelope'

// Helper to package form inputs into JSON.
// If submitter is provided (SubmitEvent.submitter), include:
//  - values.submit: submitter name (for AppMs to branch on)
//  - values.submitValue: submitter value (optional, for e.g. delete index)
export const getActionFormMessage = (form, submitter = null) => {
  // First, identify custom components and their field names to exclude their hidden inputs
  const customComponents = form.querySelectorAll('anear-counter, anear-choice-group, anear-file-upload, anear-image-upload')
  const customComponentNames = new Set()
  customComponents.forEach(component => {
    const name = component.getAttribute('name')
    if (name) {
      customComponentNames.add(name)
    }
    const filenameFieldName = component.getAttribute?.('filename-field')
    if (filenameFieldName) {
      customComponentNames.add(filenameFieldName)
    }
    const positionFieldName = component.getAttribute?.('position-field')
    if (positionFieldName) {
      customComponentNames.add(positionFieldName)
    }
    const widthFieldName = component.getAttribute?.('width-field')
    if (widthFieldName) {
      customComponentNames.add(widthFieldName)
    }
    const heightFieldName = component.getAttribute?.('height-field')
    if (heightFieldName) {
      customComponentNames.add(heightFieldName)
    }
  })

  // Read all inputs, but skip hidden inputs that belong to custom components
  // (we'll read those from the component's value property instead)
  const inputs = form.querySelectorAll(`input:not([type=submit]), select, textarea`)
  const values = {}
  inputs.forEach(input => {
    const key = input.getAttribute('name') || input.id
    if (!key) return
    // Skip hidden inputs that belong to custom components (they're internal implementation details)
    if (input.type === 'hidden' && customComponentNames.has(key)) {
      return
    }

    if (input.tagName === 'SELECT') {
      values[key] = [...input.options]
        .filter(opt => opt.selected)
        .map(opt => opt.value)
    } else {
      // For grouped radios/checkboxes, only checked controls should be serialized.
      if (input.type === 'radio' || input.type === 'checkbox') {
        if (!input.checked) return
      }
      values[key] = input.value
    }
  })

  // Read from custom form components (always use component's value property - source of truth)
  customComponents.forEach(component => {
    const name = component.getAttribute('name')
    const tag = component.tagName?.toLowerCase?.() || ''
    const opaqueImageUpload = tag === 'anear-image-upload'
      && component.opaqueMediaEnabled
      && typeof component.mediaValue === 'string'

    if (name && opaqueImageUpload) {
      // Preferred: single opaque media JSON under `name`
      values[name] = component.mediaValue || ''
    } else if (name && component.value !== undefined) {
      values[name] = String(component.value)
    }

    if (opaqueImageUpload) {
      // Skip split filename/position fields when opaque media is active
    } else {
      const filenameFieldName = component.getAttribute?.('filename-field')
      if (filenameFieldName && component.filenameValue !== undefined) {
        values[filenameFieldName] = String(component.filenameValue)
      }
      const positionFieldName = component.getAttribute?.('position-field')
      if (positionFieldName && component.positionValue !== undefined) {
        values[positionFieldName] = String(component.positionValue)
      }
    }
    const widthFieldName = component.getAttribute?.('width-field')
    if (widthFieldName && component.widthValue !== undefined) {
      values[widthFieldName] = String(component.widthValue)
    }
    const heightFieldName = component.getAttribute?.('height-field')
    if (heightFieldName && component.heightValue !== undefined) {
      values[heightFieldName] = String(component.heightValue)
    }
  })

  // Include clicked submitter (button/input[type=submit]) as a virtual field
  // so apps can branch on which submit control was used.
  try {
    if (submitter) {
      const name = submitter.getAttribute?.('name') || submitter.name
      if (name) {
        const value = submitter.getAttribute?.('value') ?? submitter.value ?? '1'
        // NOTE: if an app already has a user input named "submit", we won't overwrite it.
        if (values.submit === undefined) values.submit = name
        if (values.submitValue === undefined && value != null) values.submitValue = String(value)
      }
    }
  } catch (_e) {
    // ignore
  }
  // Get action from anear-form element (web component)
  const formAction = form.tagName.toLowerCase() === 'anear-form'
    ? form.getAttribute('action')
    : null
  if (!formAction) {
    logger.warn('getActionFormMessage: form action not found')
    return JSON.stringify({})
  }
  const formToken = form.getAttribute('data-form-token') || form.getAttribute('dataFormToken')
  return buildFormActionMessage(formAction, values, formToken)
}
